import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../../parts/SvgIcon";
import { getDateToDisplay } from "../../../../../utils/nutrition-helpers";
import { calculateStartAndEndDateOfTheWeeks } from "../../../../../utils/date-helpers";
import { useModifiedPolling } from "../../../../../hooks/useModifiedPolling";

export const MealPlannerWeekSelector = ({ query, mealPlan }) => {
  const { t } = useTranslation("athlete-portal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportionAckSent, setReportionAckSent] = useState(false);
  const { week = 1, mealPlan: mealPlanId } = query;
  const { weeks: originalWeeks = [] } = mealPlan;
  const weeks = calculateStartAndEndDateOfTheWeeks(originalWeeks);
  const selectedWeekIndex = Math.max(
    0,
    Math.min(weeks.length - 1, Number(week) - 1)
  );
  const reportion = originalWeeks?.[selectedWeekIndex]?.reportion;
  const approvalPointer = reportion?.approval || null;
  const expiresAtSec = approvalPointer?.expiresAt ?? null;

  const [remainingSec, setRemainingSec] = useState(() => {
    if (!expiresAtSec) return 0;
    const nowSec = Math.floor(Date.now() / 1000);
    return Math.max(0, expiresAtSec - nowSec);
  });

  const reportionStatus = reportion?.status || "idle";
  const isReportionChecked = reportion?.isChecked;

  useEffect(() => {
    if (!expiresAtSec) {
      setRemainingSec(0);
      return;
    }
    const tick = () => {
      const nowSec = Math.floor(Date.now() / 1000);
      setRemainingSec(Math.max(0, expiresAtSec - nowSec));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAtSec]);

  const isExpired = useMemo(() => {
    if (!expiresAtSec) return false;
    return remainingSec <= 0;
  }, [expiresAtSec, remainingSec]);

  const countdownLabel = useMemo(() => {
    if (!expiresAtSec) return null;
    const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
    const ss = String(remainingSec % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }, [expiresAtSec, remainingSec]);

  useEffect(() => {
    if (!mealPlanId || !approvalPointer) return;
    const { status, expiresAt } = approvalPointer || {};
    if (status !== "started" && !expiresAt) {
      const body = { mealPlanId, week: selectedWeekIndex, status: "started" };
      submit(`/actions/meal-planner/reportion-meal-plan-approval`, {
        method: "POST",
        body,
      });
    }
  }, [
    mealPlanId,
    selectedWeekIndex,
    approvalPointer?.status,
    approvalPointer?.expiresAt,
  ]);

  useEffect(() => {
    const isDone =
      reportionStatus === "success" || reportionStatus === "failed";
    if (
      !isDone ||
      isReportionChecked !== false ||
      reportionAckSent ||
      !mealPlanId
    ) {
      return;
    }

    setReportionAckSent(true);
    submit(`/actions/meal-planner/reportion-meal-plan-status`, {
      method: "POST",
      body: {
        mealPlanId: String(mealPlanId),
        week: week - 1,
        status: reportionStatus,
        isChecked: false,
      },
    });
  }, [reportionStatus, isReportionChecked, mealPlanId, week, reportionAckSent]);

  /**
   * Function to select the week
   * @param {*} position
   */
  const handleWeekSelector = (position) => {
    // Ensure the week is within valid bounds
    if (position >= 1 && position <= weeks.length) {
      submit(`/actions/refresh/?`, {
        method: "GET",
        body: {
          week: position,
        },
      });
    }
  };

  const sendApproval = async (status) => {
    if (!mealPlanId) {
      return;
    }
    setIsSubmitting(true);
    submit("/actions/meal-planner/reportion-meal-plan-approval", {
      method: "POST",
      body: { mealPlanId, week: selectedWeekIndex, status },
    });

    setIsSubmitting(false);
  };

  const canApproveReportioning =
    !!approvalPointer &&
    (approvalPointer.status === "started" ||
      approvalPointer.status === "pending");

  const hasPendingOperations = useMemo(() => {
    if (!originalWeeks || originalWeeks.length === 0) {
      return false;
    }

    if (mealPlan?.generation?.status === "pending") {
      return true;
    }

    return originalWeeks.some((week) => {
      if (week?.reportion?.status === "pending") {
        return true;
      }

      if (week?.regeneration?.status === "pending") {
        return true;
      }

      if (week?.days) {
        return week.days.some((day) => day?.regeneration?.status === "pending");
      }

      return false;
    });
  }, [originalWeeks, mealPlan]);

  // Add polling for meal plan updates only if there are pending operations
  useModifiedPolling({
    interval: 5000,
    enabled: true,
    field: "meal-plan",
    shouldPoll: () => hasPendingOperations,
  });

  return (
    <div className="week-selector">
      <button
        className="arrow-button -mr-2"
        onClick={() => handleWeekSelector(week - 1)}
        disabled={week <= 1} // Disable the button if the week is already at the minimum
      >
        <SvgIcon
          src="/img/new-icons/arrow-left.svg"
          alt={t("mealPlannerWeekSelector.altLeft")}
        />
      </button>
      {weeks.map(({ startDate, endDate, isCompleted }, index) => {
        let buttonText;

        if (startDate && endDate) {
          if (startDate === endDate) {
            buttonText = getDateToDisplay(startDate);
          } else {
            buttonText = `${getDateToDisplay(startDate)} - ${getDateToDisplay(
              endDate
            )}`;
          }
        } else {
          buttonText = t("mealPlannerWeekSelector.weekLabel", {
            number: index + 1,
          });
        }

        return (
          <button
            key={`${startDate}-${endDate}-${index}`}
            className={
              startDate && endDate ? "week-button w-52" : "week-button w-32"
            }
            data-button-type={week === index + 1 ? "outline" : "secondary"}
            onClick={() => handleWeekSelector(index + 1)}
          >
            <SvgIcon
              src={
                isCompleted
                  ? "/img/new-icons/checked-circle.svg"
                  : "/img/new-icons/info-circle.svg"
              }
              alt={t("mealPlannerWeekSelector.altInfo")}
              noFilterOnLight={true}
            />
            <span
              className={
                week === index + 1 ? "mx-1 opacity-100" : "mx-1 opacity-70"
              }
            >
              {buttonText}
            </span>
            <a
              onClick={(e) => e.stopPropagation()}
              href={`/actions/refresh?modal[type]=duplicate-meal-plan-week&week=${
                index + 1
              }`}
            >
            <div>
              <SvgIcon
                src="/img/new-icons/duplicate.svg"
                alt={t("mealPlannerWeekSelector.altDuplicate")}
              />
            </div>
            </a>
          </button>
        );
      })}
      <button
        className="arrow-button -ml-2"
        disabled={week >= weeks.length} // Disable the button if the week is already at the maximum
        onClick={() => handleWeekSelector(week + 1)}
      >
        <SvgIcon
          src="/img/new-icons/arrow-right.svg"
          alt={t("mealPlannerWeekSelector.altRight")}
        />
      </button>
      {/* {canApproveReportioning && !isExpired && (
        <div className="ml-4 flex items-center gap-2">
          <button
            type="button"
            data-button-type="primary"
            disabled={!canApproveReportioning || isSubmitting}
            title={
              !canApproveReportioning
                ? "No pending changes to confirm"
                : undefined
            }
            onClick={() => sendApproval("confirmed")}
          >
            {isSubmitting ? "Submitting..." : "Confirm reportioning"}
          </button>
          <button
            type="button"
            data-button-type="outline"
            disabled={!canApproveReportioning || isSubmitting}
            title={
              !canApproveReportioning
                ? "No pending changes to revert"
                : undefined
            }
            onClick={() => sendApproval("reverted")}
          >
            {isSubmitting
              ? "Submitting..."
              : `Revert reportioning${
                  countdownLabel ? ` (${countdownLabel})` : ""
                }`}
          </button>
        </div>
      )} */}
    </div>
  );
};

MealPlannerWeekSelector.propTypes = {
  mealPlan: PropTypes.object,
  query: PropTypes.object,
};
